import { CobrowsingService } from '@agent/core/cobrowsing/cobrowsing.service';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { filter, map, merge, switchMap, tap } from 'rxjs';

import { Direction, MediaType } from '@contact/common-transport-http-util-types';
import { BookingFacade } from '@contact/tiles-agent-feature-core';
import { BookingStatus, CngTileEffect, isNotNullOrUndefined, TileId, ConferenceAction } from '@contact/upgrade';

import { CobrowsingAction, CobrowsingDispatcher } from './cobrowsing.dispatcher';

@Injectable({
  providedIn: 'root',
})
export class CobrowsingEffect extends CngTileEffect {
  readonly callBookingStatus$ = merge(
    this.bookingFacade.created$.pipe(isNotNullOrUndefined()),
    this.bookingFacade.success$.pipe(isNotNullOrUndefined())
  ).pipe(filter(({ current, booking }) => booking.channel.type === MediaType.PHONE || booking.channel.type === MediaType.UNITARY_PHONE));

  readonly initCobrowsingOnCallStart = createEffect(() =>
    this.callBookingStatus$.pipe(
      filter(({ current }) => current === BookingStatus.IN_PROCESS),
      switchMap(({ booking }) =>
        this.phoneCobrowsingService
          .initializeCobrowsingForBooking(booking.responseId)
          .pipe(map(info => this.cobrowsingDispatcher.initCobrowsing(info)))
      )
    )
  );

  readonly removeCobrowsingOnCallEnd = createEffect(() =>
    this.callBookingStatus$.pipe(
      filter(
        ({ current }) =>
          current === BookingStatus.POST_PROCESSING || current === BookingStatus.INTER_CONTACT || current === BookingStatus.CLOSED
      ),
      map(({ booking }) => this.cobrowsingDispatcher.removeCobrowsing(booking.responseId))
    )
  );

  readonly onStartCobrowsing = createEffect(() =>
    this.actions$.pipe(
      ofType(CobrowsingAction.StartCobrowsing),
      switchMap(({ info }) => this.phoneCobrowsingService.startCobrowsingFromCode(info.bookingId, info.sessionCode || '')),
      map(cobrowsingInfo => {
        if (cobrowsingInfo?.errorMessageKey) {
          return this.cobrowsingDispatcher.startCobrowsingError(cobrowsingInfo);
        } else {
          return this.cobrowsingDispatcher.cobrowsingStarted(cobrowsingInfo);
        }
      })
    )
  );
  readonly onCobrowsingStarted = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CobrowsingAction.StartCobrowsing),
        switchMap(({ info }) => this.bookingFacade.getById(info.bookingId)),
        isNotNullOrUndefined(),
        tap(booking => {
          this.phoneCobrowsingService.sendStartInfoForReporting(booking);
        })
      ),
    { dispatch: false }
  );

  readonly onForwardCall = createEffect(() =>
    this.actions$.pipe(
      ofType(ConferenceAction.Start),
      map(({ id }) => this.cobrowsingDispatcher.removeCobrowsing(id))
    )
  );

  readonly onRemoveCobrowsing = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CobrowsingAction.RemoveCobrowsing),
        tap(({ bookingId }) => this.phoneCobrowsingService.cleanAfterInteractionEnd(bookingId))
      ),
    { dispatch: false }
  );

  readonly onEndCobrowsing = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CobrowsingAction.EndCobrowsing),
        tap(({ bookingId }) => this.phoneCobrowsingService.endSession(bookingId))
      ),
    { dispatch: false }
  );

  readonly onCobrowsingEnded = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CobrowsingAction.CobrowsingEnded),
        switchMap(({ bookingId }) => this.bookingFacade.getById(bookingId)),
        isNotNullOrUndefined(),
        tap(booking => {
          this.phoneCobrowsingService.sendEndInfoForReporting(booking);
        })
      ),
    { dispatch: false }
  );

  constructor(
    private readonly bookingFacade: BookingFacade,
    private readonly phoneCobrowsingService: CobrowsingService,
    private readonly cobrowsingDispatcher: CobrowsingDispatcher,
    private readonly actions$: Actions
  ) {
    super(TileId.CSR);
  }
}
